import { slideUp, slideToggle, slideOut, slideDown } from './slide';
import { setCookieToDate } from './cookies-helper';
import { debounce } from '../utils/utils';
import { init, browserTracingIntegration } from '@sentry/browser';

// URL

window.currentURL = 'https://ottonova.de';
window.backendApiUrl = 'https://www.ottonova.de/api';
window.assetPath = '/assets';
window.captain = 'FCMIFKOVNQLMA3ET';
window.language = document.documentElement.lang;
window.en = document.documentElement.lang === 'en';
window.utmz = {};
window.utmi = {};
window.mycaptain = window.location.search.indexOf('captaincaps') > -1;
window.sentryEnvironment = 'live';
let tracesSampleRateValue = 0.2;

if (window.location.host.indexOf('staging.on.ag') > -1) {
  window.currentURL = 'https://staging.on.ag';
  window.backendApiUrl = 'https://staging.backend.ottonova.de/api';
  window.captain = 'FCMIFKOVNOS05GLI';
  window.sentryEnvironment = 'staging';
  tracesSampleRateValue = 0.01;
} else if (window.location.host.indexOf('test.on.ag') > -1 || window.location.host.indexOf('localhost') > -1) {
  window.currentURL = 'https://test.on.ag';
  window.backendApiUrl = 'https://test.backend.ottonova.de/api';
  window.captain = 'FCMIFKOVNOS05GLI';
  window.sentryEnvironment = window.location.host.indexOf('test.on.ag') > -1 ? 'test' : 'localhost';
  tracesSampleRateValue = 0.005;
  if (window.location.search.indexOf('gofullsentry') > -1) {
    tracesSampleRateValue = 1;
  }
}

init({
  dsn: window.craftConfig.global.sentryDsn,
  environment: window.sentryEnvironment,
  release: window.craftConfig?.cms,
  tracesSampleRate: tracesSampleRateValue,
  integrations: [browserTracingIntegration()],
  tracePropagationTargets: [/^\//, /^https:\/\/services(-staging|-test)?\.(on\.ag|ottonova\.de)\//, /^https:\/\/www\.ottonova\.de\/api\//, /^https:\/\/(staging|test)\.backend\.ottonova\.de\/api\//],
  allowUrls: [/https?:\/\/((test|staging|www)\.)?(on\.ag|ottonova\.de)|https?:\/\/localhost/],
  denyUrls: [
    /graphql\.usercentrics\.eu/,
    /api\.trustpilot\.com/,
    // GTM container script (third-party tags run through it, not our code) - WEBSITE-E8/DY/80
    // are duplicate "Cannot read properties of null" errors from a tag inside this container.
    /\/metrics\/devu9bjwwtbwg\.js/
  ],
  ignoreErrors: [
    // Thrown by Meta's own in-app browser bridge script (injected by Instagram/Facebook),
    // not by our code — window.webkit.messageHandlers is never referenced in this codebase.
    /window\.webkit(\.messageHandlers)?/,
    // googletag is loaded and driven entirely by the GTM container (not this codebase) —
    // WEBSITE-8Z, an interval inside a GTM tag firing before the GPT library is ready.
    /googletag is not defined/
  ],
  beforeSend: event => {
    // WEBSITE-GF: recursive DOM walk inside a GTM tag's attribute handler on refer-friends,
    // no matching recursive code exists in this codebase. Scoped to that page + message so a
    // genuine stack overflow elsewhere in the app still gets reported.
    const isRefererFriendsStackOverflow =
      event.transaction?.startsWith('/en/ml/refer-friends') && event.exception?.values?.some(value => value.type === 'RangeError' && value.value?.includes('Maximum call stack size exceeded'));
    if (isRefererFriendsStackOverflow) {
      return null;
    }
    return event;
  }
});

//Handle ON APP Calls via mobile=1 URL Parameter
try {
  if (window.location.search.indexOf('mobile=1') > -1) {
    window.sessionStorage.setItem('onAppCall', new Date(Date.now()).toLocaleString().split(',')[0]);
  }
  window.onAppCall = window.sessionStorage.getItem('onAppCall');
} catch (e) {
  window.onAppCall = null;
}
if (onAppCall && new Date(Date.now()).toLocaleString().split(',')[0] === onAppCall) {
  // Hide the header
  if (document.getElementById('header')) {
    document.getElementById('header').style.display = 'none';
    // Adjust spacing on hero section
    document.querySelector('section.hero')?.style.setProperty('--section-spacing-top', '42px');
  }

  // Hide the hero link back
  const heroLinkBack = document.querySelector('.hero-link-back-block');
  if (heroLinkBack) {
    heroLinkBack.querySelector('a.link')?.style.setProperty('display', 'none');
    heroLinkBack.style.setProperty('--section-spacing-top', '0');
    heroLinkBack.style.setProperty('padding-top', '20px');
  }

  // Hide the footer
  const footer = document.getElementById('footer');
  if (footer) {
    footer.querySelector('.navigation')?.style.setProperty('display', 'none');

    // Hide trust and social sections
    const trust = footer.querySelector('.trust');
    if (trust) {
      trust.style.display = 'none';
    }
    const social = footer.querySelector('.social');
    if (social) {
      social.style.display = 'none';
    }
  }

  // Adjust position of stickyNavigationTabs and jump-nav-block
  if (document.querySelector('.stickyNavigationTabs-block')) {
    document.querySelector('.stickyNavigationTabs-block').style.top = '0';
  }
  if (document.querySelector('.jump-nav-block')) {
    document.querySelector('.jump-nav-block').style.top = '0';
  }
}

const navigationToggle = document.querySelector('#header .navigation-toggle'),
  actionToggle = document.querySelector('#header .action a.phone'),
  mobileNav = document.querySelector('#header nav.hide--lg');

let headerHeight = 0;

if (document.body.contains(navigationToggle)) {
  const body = document.querySelector('body');
  const toggleNavigation = isOpenForce => {
    const isOpen = navigationToggle.classList.contains('active');
    const newIsOpen = isOpenForce !== undefined ? Boolean(isOpenForce) : !isOpen;
    /** @type {HTMLElement} */
    const languageHintMobile = document.querySelector('.language-hint-mobile');
    if (isOpen === newIsOpen) {
      return;
    }
    body.classList.toggle('modal-open', newIsOpen);
    if (!newIsOpen) {
      navigationToggle.ariaLabel = en ? 'Open navigation' : 'Navigation öffnen';
      mobileNav.ariaHidden = 'true';
    } else {
      navigationToggle.ariaLabel = en ? 'Close navigation' : 'Navigation schließen';
      mobileNav.ariaHidden = 'false';
    }
    navigationToggle.classList.toggle('active', newIsOpen);

    if (newIsOpen) {
      if (languageHintMobile) {
        languageHintMobile.classList.add('nopacity');
      }
      headerHeight = header.offsetHeight;
      header.style.setProperty('--header-background-y', window.innerHeight - headerHeight + 'px');
      header.style.setProperty('--header-background-scale', window.innerHeight / headerHeight);
      header.classList.add('open');
      setTimeout(() => {
        header.style.setProperty('--header-background-duration', '0s');
        header.style.setProperty('--header-background-y', 0);
        header.style.setProperty('--header-background-scale', 1);
        header.style.bottom = 0;
        header.style.setProperty('--header-background-duration', '.35s');
        header.classList.add('opened');
      }, 350);
    } else {
      if (languageHintMobile) {
        languageHintMobile.classList.remove('nopacity');
      }
      header.style.setProperty('--header-background-delay', '.35s');
      header.style.setProperty('--header-background-y', headerHeight - window.innerHeight + 'px');
      header.style.setProperty('--header-background-scale', headerHeight / window.innerHeight);
      setTimeout(() => {
        header.style.setProperty('--header-background-duration', '0s');
        header.style.bottom = 'auto';
        header.style.setProperty('--header-background-y', 0);
        header.style.setProperty('--header-background-scale', 1);
        setTimeout(() => {
          header.style.setProperty('--header-background-duration', '.35s');
          header.style.setProperty('--header-background-delay', '0s');
        }, 50);
      }, 350);
      header.classList.remove('opened', 'open');
    }
  };
  // mobile Navigation hamburger toggle
  navigationToggle.addEventListener('click', () => toggleNavigation());
  window.addEventListener('scrollStart', () => toggleNavigation(false));

  const header = document.querySelector('#header');

  if (header) {
    const mainNavElements = header.querySelectorAll('.box-mobile > a');
    window.addEventListener('openMenuMobile', () => toggleNavigation(true));

    // mobile Navigation section toggle
    mainNavElements.forEach(elem => {
      elem.addEventListener('click', event => {
        if (window.innerWidth >= 1200) {
          if ('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) {
            const actualHoverElement = header.querySelector('.box-mobile.hover');
            event.preventDefault();
            event.stopPropagation();
            if (actualHoverElement) {
              actualHoverElement.classList.remove('hover');
              if (actualHoverElement !== elem.parentNode) {
                elem.classList.add('dontRedirect');
                elem.parentNode.classList.add('hover');
              }
            } else {
              elem.classList.add('dontRedirect');
              elem.parentNode.classList.add('hover');
            }
          }
        } else {
          event.preventDefault();
          elem.classList.add('dontRedirect');
          elem.classList.toggle('active');
          slideToggle(elem.nextElementSibling);
        }
      });
    });

    // Remove hover class when clicking outside the navigation (on touch devices)
    document.addEventListener('click', event => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
      if (isTouchDevice && event.target) {
        const hoverElement = header.querySelector('.box-mobile.hover');
        if (hoverElement) {
          hoverElement.classList.remove('hover');
        }
      }
    });
  }

  // mobile dropdown consultation toggle
  if (window.innerWidth < 993) {
    actionToggle.classList.add('dontRedirect');
    actionToggle.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      let parentAction = actionToggle.parentNode,
        dropdown = document.querySelector('#header .action .dropdown');
      parentAction.classList.toggle('open');
      const outsiderClicker = function (ev) {
        const clickInside = dropdown.contains(ev.target);
        const clickButton = actionToggle.contains(ev.target);
        if (!clickInside || !clickButton) {
          parentAction.classList.toggle('open');
          document.removeEventListener('click', outsiderClicker);
        }
      };
      document.addEventListener('click', outsiderClicker, false);
    });
  }
}

//english language hint
const showLanguageHint = document.documentElement.hasAttribute('data-show-language-hint');

/** @type {HTMLElement} */
const languageHintDesktop = document.querySelector('.language-hint-desktop');

/** @type {HTMLElement} */
const languageHintMobile = document.querySelector('.language-hint-mobile');

if (showLanguageHint) {
  //init close Button
  const initCloseButton = languageHint => {
    const close = languageHint.querySelector('.close');
    close.addEventListener('click', () => window.dispatchEvent(new Event('languageHintCLosed')));
  };

  if (languageHintMobile) {
    initCloseButton(languageHintMobile);
    languageHintMobile.style.display = 'flex';
  }

  if (languageHintDesktop) {
    initCloseButton(languageHintDesktop);
    languageHintDesktop.style.display = 'flex';
  }

  window.addEventListener('languageHintCLosed', () => {
    setCookieToDate('language-hint-closed', true);
    const hero = document.querySelector('.hero.site-block');
    if (hero && languageHintMobile) {
      hero.style.setProperty('transition', 'padding-top 0.25s ease');
    }
    if (languageHintDesktop) {
      slideOut(languageHintDesktop);
    }
    if (languageHintMobile) {
      slideUp(languageHintMobile);
    }
    setTimeout(() => {
      document.documentElement.removeAttribute('data-show-language-hint');
      if (hero && languageHintMobile) {
        requestAnimationFrame(() => {
          hero.style.removeProperty('transition');
        });
      }
    }, 250);
  });
}

if (showLanguageHint && languageHintMobile) {
  header.style.setProperty('--header-before-height', `${header.offsetHeight - languageHintMobile.offsetHeight}px`);
  document.body.style.setProperty('--language-hint-height', `${languageHintMobile.offsetHeight}px`);
  window.addEventListener(
    'resize',
    debounce(() => {
      header.style.setProperty('--header-before-height', `${header.offsetHeight - languageHintMobile.offsetHeight}px`);
      document.body.style.setProperty('--language-hint-height', `${languageHintMobile.offsetHeight}px`);
    }, 300)
  );
}
//form progress text
window.progressText = en
  ? `<span>Loading <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`
  : `<span>Einen Moment <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`;

window.preparationText = en
  ? `<span>Preparing <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`
  : `<span>Vorbereitung läuft <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`;

function addTabFocusClass() {
  document.body.classList.add('tab-navigation-active');
  removeTabListener();
}

function tabFocusHandler(event) {
  if (event.key !== 'Tab' || event.code !== 'Tab') {
    return;
  }
  const focusSwitch = document.querySelector('#header .switch input[name="focus"]');
  focusSwitch.checked = true;
  try {
    localStorage.setItem('focus', 'true');
  } catch (e) {
    /* blocked in strict privacy mode */
  }
  addTabFocusClass();
}

document.addEventListener('keydown', tabFocusHandler);

const removeListener = ['mousemove', 'mousedown', 'touchstart'];

function removeTabListener() {
  removeListener.forEach(event => document.removeEventListener(event, removeTabListener));
  document.removeEventListener('keydown', tabFocusHandler);
}

removeListener.forEach(event => document.addEventListener(event, removeTabListener));

const skipNavLink = document.querySelector('#header .keyboard-hidden a');
const firstFocusableElement = document.querySelector('main')?.querySelector('a, button, input, [tabindex]:not([tabindex^="-"])');

if (skipNavLink && firstFocusableElement) {
  skipNavLink.addEventListener('click', event => {
    event.preventDefault();
    firstFocusableElement.focus();
  });
} else if (skipNavLink) {
  skipNavLink.remove();
}

// Reload once on stale chunk errors after a deploy
window.addEventListener('unhandledrejection', event => {
  const msg = event?.reason?.message ?? '';
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed') || msg.includes('error loading')) {
    const reloadKey = 'chunk-reload';
    try {
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
      }
    } catch (e) {
      // sessionStorage blocked
    }
  }
});
